import { Component, NgZone, AfterViewInit } from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { Platform, NavController } from '@ionic/angular';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Location } from '@angular/common';
import { filter, debounceTime } from 'rxjs/operators';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from './services/permission.service';
import { PushNotificationsService } from './services/push-notifications.service';
import { PosthogService } from './services/posthog.service';
import { DataLayerService } from './services/data-layer.service';
import { StrapiService } from './services/strapi.service';
import { OverviewProjectService } from './services/overview-project.service';
import { OveralMessageComponent } from './shared/components/overal-message/overal-message.component';
import { AlertService } from './shared/components/alert/alert.service';
import { ToastService } from './shared/components/toast/toast.service';
import { AuthService, NEXUS_MESSAGE_SOURCE, MSG_REQUEST_LOGOUT } from './services/auth.service';
import { PENDING_LLEIDANET_SIGN_KEY } from './shared/components/formalize-signature/formalize-signature.component';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements AfterViewInit {
  isLogin: boolean;
  subscription: any;
  counter = 0;
  isLast: boolean;

  constructor(
    private platform: Platform,
    private router: Router,
    private zone: NgZone,
    public location: Location,
    private navCtrl: NavController,
    private storage: Storage,
    private permission: PermissionService,
    private pushNotifications: PushNotificationsService,
    private posthogService: PosthogService,
    private dataLayerService: DataLayerService,
    private strapiService: StrapiService,
    private alertService: AlertService,
    private toastService: ToastService,
    private translate: TranslateService,
    private overviewProjectService: OverviewProjectService,
    public authService: AuthService
  ) {
    this.posthogService.init();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      debounceTime(1000)
    ).subscribe((event: any) => {
      if (event.url === '/es/acceso' || event.url === '/en/login' || event.url === 'accés') {
        this.isLogin = true;
      } else {
        this.isLogin = false;
      }
      this.posthogService.pageView(event.url);
      this.dataLayerService.pushPageView();
    });
    this.initializeApp();

    this.platform.ready().then(() => {
      if (Capacitor.isNativePlatform()) {
        SplashScreen.hide().catch(() => {});
        StatusBar.setStyle({ style: Style.Light }).catch(() => {});
        if (this.platform.is('android')) {
          StatusBar.setBackgroundColor({ color: '#DBB4C6' }).catch(() => {});
        }
        ScreenOrientation.lock({ orientation: 'portrait' }).catch(() => {});
      }
      this.pushNotifications.activateOneSignal();
    });
  }

  ngAfterViewInit() {
    this.authService.checkImpersonation();
    this.listenForNexusImpersonationMessages();
    this.permission.requestATT();
    this.removeServiceWorker();
    //this.authService.isLogged();
    this.listenOnHardwareBackButton();
    this.checkPendingLleidanetSign();
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationStart) {
        if (
          (val.url === '/es/registro' || val.url === '/en/register') &&
          this.platform.is('android') &&
          Capacitor.getPlatform() !== 'web'
        ) {
          StatusBar.setBackgroundColor({ color: '#FFFFFF' });
        } else if (Capacitor.getPlatform() !== 'web' && this.platform.is('android')) {
          StatusBar.setBackgroundColor({ color: '#FFFFFF' });
        }
        // this.posthogService.pageView("$pageview");
        this.controlChatClosed();
      }
    });
    if (Capacitor.getPlatform() !== 'web') {
      this.strapiService.getIfLockedNativeApp().subscribe(resp => {
        const info = resp.data.attributes;
        if ((info.android && Capacitor.getPlatform() === 'android') || (info.ios && Capacitor.getPlatform() === 'ios')) {
          const modalParams = {
            text: `<h2>Aplicación en mantenimiento.</h2><p>${info.text}</p>`,
            image: 8
          };
          this.alertService.showModal(OveralMessageComponent, modalParams, '', '', true).subscribe((modal: any) => {
            modal.onClose().subscribe();
          });
        }
      });
    }
    this.pushNotifications.checkPushNotifications();
  }

  controlChatClosed() {
    if (Capacitor.getPlatform() === 'web' && window.HubSpotConversations === undefined) {
      this.timerChatClosed();
    } else {
      this.storage.get('CHAT_OPENED').then((val) => {
        if (val !== true) {
          setTimeout(() => {
            try { window.HubSpotConversations.widget.remove(); } catch { }
          }, 400);
        }
      });
    }
  }

  timerChatClosed() {
    setTimeout(() => {
      this.controlChatClosed();
    }, 100);
  }

  async initializeApp() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        // Example url: https://beerswift.app/tabs/tab2
        // slug = /tabs/tab2
        const slug = event.url.split("fundeen.com").pop();
        if (slug) {
          this.router.navigateByUrl(slug);
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
  }

  private listenOnHardwareBackButton() {
    if (this.platform.is('android')) {
      if (this.isLast) {
        const appStr = 'app';
        navigator[appStr].exitApp();
      } else {
        App.addListener('backButton', ({ canGoBack }) => {
          if (canGoBack) {
            // Si hay historial, volver
            window.history.back();
          } else {
            // Si no hay historial, puedes cerrar la app o implementar lógica personalizada
            navigator['app'].exitApp(); // Cierra la aplicación
          }
        });
      }
      // this.platform.backButton.subscribeWithPriority(0, () => {
      //   if (this.isLast) {
      //     const appStr = 'app';
      //     navigator[appStr].exitApp();
      //   }
      //   //this.navCtrl.back();
      // });
      this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
        if (
          event.url === '/es/portfolio' ||
          event.url === '/en/portfolio' ||
          event.url === '/ca/portfolio' ||
          event.url === '/es/acceso' ||
          event.url === '/en/login' ||
          event.url === '/ca/accés'
        ) {
          this.isLast = true;
        } else {
          this.isLast = false;
        }
        this.storage.get('ISFIRST').then((val) => {
          if (val === null && this.counter === 1) {
            this.isLast = true;
            this.storage.set('ISFIRST', 'first');
          }
        });
        if (event.navigationTrigger !== 'popstate') {
          this.counter++;
        }
      });
    }
  }
  removeServiceWorker() {
    navigator.serviceWorker.getRegistrations().then(function (registrations) {
      for (let registration of registrations) {
        registration.unregister();
      }
    });
    /*
    if ('caches' in window) {
      caches.keys().then(function (keyList) {
        return Promise.all(
          keyList.map(function (key) {
            return caches.delete(key);
          })
        );
      });
    }*/
  }

  /** Escucha a fundeen-nexus pidiendo cerrar la sesión de impersonación
   * (por ejemplo, al cerrar su modal de vista previa o al caducar el token
   * desde su lado). Solo actúa si de verdad estamos impersonando. */
  private listenForNexusImpersonationMessages() {
    window.addEventListener('message', (event: MessageEvent) => {
      const data = event?.data;
      if (data?.source !== NEXUS_MESSAGE_SOURCE || data?.type !== MSG_REQUEST_LOGOUT) return;
      this.authService.handleParentLogoutRequest();
    });
  }

  /** En Android, mientras el navegador de Lleidanet está abierto firmando una inversión, el SO puede matar el
   * proceso de la app (process death por backgrounding) y perder todo el estado en memoria, dejando la
   * inversión sin finalizar aunque la firma se haya completado. FormalizeSignatureComponent guarda un registro
   * mínimo en Storage justo antes de abrir Lleidanet; al relanzar la app comprobamos aquí si quedó una firma
   * pendiente y, si ya está firmada, completamos la inversión automáticamente. */
  private async checkPendingLleidanetSign() {
    if (Capacitor.getPlatform() !== 'android') { return; }
    const pending = await this.storage.get(PENDING_LLEIDANET_SIGN_KEY);
    if (!pending) { return; }
    this.overviewProjectService.getLleidanetSignStatus(pending.lleidanetID).subscribe(resp => {
      if (resp?.signature_status === 'signed') {
        this.overviewProjectService.finishInvest(pending.investId).subscribe(() => {
          this.storage.remove(PENDING_LLEIDANET_SIGN_KEY);
          this.toastService.showToast(this.translate.instant('SIGN.OK'), 'success', true);
        }, () => {
          this.storage.remove(PENDING_LLEIDANET_SIGN_KEY);
        });
      } else {
        this.storage.remove(PENDING_LLEIDANET_SIGN_KEY);
      }
    }, () => {
      this.storage.remove(PENDING_LLEIDANET_SIGN_KEY);
    });
  }

}
